import request from '@/utils';

export type CloudStreamMode = 'ws_scrcpy' | 'ws_room';

const DEFAULT_STREAM_MODE: CloudStreamMode = 'ws_scrcpy';
const RTC_UUID_STORAGE_KEY = 'cloud_android2_rtc_uuid';

type RtcTokenResponse = {
    rtc_vendor?: string;
    pull_url?: string;
};

export type RtcRouteDecision = {
    mode: CloudStreamMode;
    vendor?: string;
    pullUrl?: string;
};

function createFallbackUuid(): string {
    return `pc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateRtcClientUuid(): string {
    const stored = window.localStorage.getItem(RTC_UUID_STORAGE_KEY);
    if (stored) {
        return stored;
    }
    const next =
        typeof window.crypto?.randomUUID === 'function' ? window.crypto.randomUUID() : createFallbackUuid();
    window.localStorage.setItem(RTC_UUID_STORAGE_KEY, next);
    return next;
}

export async function resolveRtcRoute(padCode: string): Promise<RtcRouteDecision> {
    if (!padCode) {
        throw new Error('Missing pad code for RTC route request');
    }
    const payload = {
        uuid: getOrCreateRtcClientUuid(),
        pad_code: padCode,
        supported_vendors: ['ws_room'],
    };
    const response = await request.post('/rtc/apply_for_rtc_token', payload);
    const rtcData = response?.data?.data as RtcTokenResponse | undefined;
    const vendor = rtcData?.rtc_vendor?.trim().toLowerCase();
    const pullUrl = rtcData?.pull_url?.trim();

    if (!rtcData) {
        throw new Error('RTC token response is empty');
    }

    if (vendor === 'ws_room' && pullUrl) {
        return {
            mode: 'ws_room',
            vendor,
            pullUrl: rewriteWsRoomPullUrl(pullUrl),
        };
    }

    return {
        mode: DEFAULT_STREAM_MODE,
        vendor,
    };
}

/**
 * ws_room pullUrl 改写：
 *   1) 若配置了 VUE_APP_WS_ROOM_WSS_BASE，则用它替换 pullUrl 的 origin（+ 基础 path）
 *      目的：把后端返回的 ws://<ip>:<port>/... 走到前端同域的 wss 反代网关
 *   2) 否则，若当前页面是 HTTPS 且后端给出的是 ws://，尝试升级为 wss://（远端自带证书的兜底）
 *   3) 否则原样返回
 */
function rewriteWsRoomPullUrl(rawUrl: string): string {
    try {
        const configBase = (process.env.VUE_APP_WS_ROOM_WSS_BASE || '').trim().replace(/\/$/, '');
        if (configBase) {
            // 保留 path 里 /rtc/ws_room 之后的部分与 query，替换 origin + 前缀
            const u = new URL(rawUrl);
            // 保留 query
            const suffix = `${u.pathname}${u.search}${u.hash}`;
            // 配置值自带协议（wss://xxx/prefix）→ 直接用其作为前缀，附加 suffix
            return `${configBase}${suffix}`;
        }
        if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && rawUrl.startsWith('ws://')) {
            return 'wss://' + rawUrl.slice('ws://'.length);
        }
        return rawUrl;
    } catch (_err) {
        return rawUrl;
    }
}
